import { useEffect } from 'react'
import * as React from 'react'

import { registerMyDesign } from '~/api/myDesign'
import { useMutation } from '~/hooks/useMutation'
import type { LoginSession } from '~/hooks/useSession'
import { useToast } from '~/hooks/useToast'
import type { ReadyCustomization } from '~/types/Customization'
import type { Design } from '~/types/Design'
import type { MyDesign } from '~/types/MyDesign'
import type { Product } from '~/types/Product'
import { toRequestPayload } from '~/utils/customization'

interface Params {
  customization: ReadyCustomization
  design: Design
  product: Product

  session: LoginSession
}

export function useMyDesignCreation({
  onCreated
}: {
  onCreated?(myDesign: MyDesign): void
} = {}) {
  const toast = useToast()

  const mutation = useMutation(
    async ({ customization, design, product, session }: Params) =>
      registerMyDesign(
        toRequestPayload(customization, design, product),
        session.accessToken.token
      )
  )

  // マイデザイン新規保存が成功・失敗したら、その結果に応じた処理を行う
  // NOTE: status をディスクリミネータにして分岐
  useEffect(() => {
    switch (mutation.status) {
      case 'success': {
        onCreated?.(mutation.data)
        try {
          const key = `server-${mutation.data.id}`
          const w = window as unknown as {
            myDesignsJustAdded?: Set<string> | string[]
          }
          const buf: Set<string> =
            w.myDesignsJustAdded instanceof Set
              ? w.myDesignsJustAdded
              : new Set(
                  Array.isArray(w.myDesignsJustAdded)
                    ? w.myDesignsJustAdded
                    : []
                )
          buf.add(key)
          w.myDesignsJustAdded = buf
          window.dispatchEvent(
            new CustomEvent('my-designs:added', {
              detail: { origin: 'server', id: mutation.data.id }
            })
          )
        } catch {
          /* noop */
        }
        try {
          window.dispatchEvent(new Event('my-designs:open'))
        } catch {
          /* noop */
        }
        // 一覧のリアルタイム更新（グローバルイベント経由）
        try {
          window.dispatchEvent(new Event('my-designs:updated'))
        } catch {
          /* noop */
        }
        return
      }
      case 'failed': {
        toast.show({
          variant: 'error',
          title: 'マイデザインへの保存に失敗しました',
          description:
            mutation.error instanceof Error
              ? mutation.error.message
              : String(mutation.error)
        })
        return
      }
    }
  }, [mutation, onCreated, toast.show])

  return mutation
}
